import React from "react";
import { Popper, Paper } from "@mui/material";
import CodeBlock from "./CodeBlock";

/**
 * One shared cursor-following tooltip for a whole grid column: rather than mounting a Popper per cell
 * (slow, flaky hover), cells push their content + pointer position into this imperative handle and it
 * follows the cursor. Isolating the hover state here keeps mouse moves from re-rendering the DataGrid.
 */
export type CodeHoverApi = { move: (code: string, x: number, y: number) => void; hide: () => void };

const POPPER_MODIFIERS = [
    { name: "offset", options: { offset: [12, 12] } },
    { name: "preventOverflow", options: { padding: 8 } },
    { name: "flip", enabled: true },
];

type Props = {
    /** Portal target — pass the pop-out window's body so the tooltip renders in the correct window. */
    container?: Element;
    /** Prism grammar for the hovered content — "jsx" for template fragments, "json" for trace/data values. */
    language?: string;
    maxHeight?: number;
};

export const CodeHoverLayer = React.forwardRef<CodeHoverApi, Props>(({ container, language, maxHeight = 320 }, ref) => {
    const [hover, setHover] = React.useState<{ code: string; x: number; y: number } | null>(null);

    React.useImperativeHandle(ref, () => ({
        move: (code, x, y) => setHover({ code, x, y }),
        hide: () => setHover(null),
    }), []);

    // Virtual anchor at the pointer — Popper lays the tooltip out relative to it.
    const anchorEl = React.useMemo(
        () =>
            hover && {
                getBoundingClientRect: () =>
                    ({ x: hover.x, y: hover.y, top: hover.y, left: hover.x, right: hover.x, bottom: hover.y, width: 0, height: 0, toJSON: () => ({}) } as DOMRect),
            },
        [hover]
    );

    return (
        <Popper
            open={!!hover}
            anchorEl={anchorEl ?? undefined}
            container={container}
            placement="bottom-start"
            modifiers={POPPER_MODIFIERS}
            style={{ pointerEvents: "none", zIndex: 2000 }}
        >
            {hover && (
                <Paper sx={{ p: 0, overflow: "hidden", maxWidth: 640 }}>
                    <CodeBlock code={hover.code} language={language} maxHeight={maxHeight} />
                </Paper>
            )}
        </Popper>
    );
});
